// Quality Signals — Constellations (semantic clustering) API client.
// Backend: app/routes/signals_reports.py GET /api/admin/signals/clusters.
// Server-side clustering via gurucloud-kb kb.cluster() (per-field, auto labels).

import api from './api';

export interface ClusterMember {
  id: string;
  snippet: string;
  value: string | null;
  customer_no?: string | null;
  observed_at?: string | null;
  signal_type?: string | null;
  // Distance to the cluster centroid (vector clusters only; smaller = more
  // central). Large clusters list a DIVERSE member sample (server-side
  // member_sample="diverse"), served sorted ascending by distance.
  distance?: number | null;
}

// Event-time pulse: computed from metadata.observed_at (the source email's own
// date) over the cluster's sampled members — NOT ingest time.
export interface ClusterTemporal {
  first_seen: string;
  last_seen: string;
  recent_count: number;
  dated_count: number;
  sample_size: number;
  burst_score: number;
  heat: 'hot' | 'warm' | 'cold';
}

export interface Cluster {
  cluster_id: number;
  label: string;
  size: number;
  keywords: string[];
  members: ClusterMember[];
  is_noise?: boolean;
  // Emergent group: the platform re-clustered HDBSCAN noise into this cluster
  // (outlier_strategy="subcluster") — a real group, but not a dense topic core.
  from_noise?: boolean;
  // This cluster's spread is an outlier vs its peers — likely a catch-all.
  low_cohesion?: boolean;
  mean_member_distance?: number | null;
  temporal: ClusterTemporal | null;
}

export interface ConstellationDimension {
  key: string;
  label: string;
  kind: 'semantic' | 'facet';
}

// Fixed trailing windows the picker offers. Resolved SERVER-SIDE (a UTC-
// midnight start, open-ended end) so a preset lands on exactly the cache key
// the beat warmer precomputes — the common views serve instantly.
export type WindowPreset = '7d' | '14d' | '30d';
export const WINDOW_PRESET_KEYS: readonly WindowPreset[] = ['7d', '14d', '30d'];

// The thresholds behind every cluster's temporal.heat in a payload — the
// legend renders from these so it can never drift from the classifier.
export interface ClusterHeatRules {
  hot_days: number;
  hot_min_recent: number;
  hot_share: number;
  warm_days: number;
}

export interface ClustersResponse {
  dimension: string;
  label: string;
  method: string;
  algorithm: string | null;
  cluster_count: number;
  clustered_count: number;
  noise_count: number;
  entry_count: number;
  silhouette: number | null;
  clusters: Cluster[];
  dimensions: ConstellationDimension[];
  // Stale-while-revalidate freshness story: when the served clustering was
  // computed, and whether a background recompute is pending right now.
  computed_at?: string | null;
  refreshing?: boolean;
  // True when this parameter-set has never been computed: the payload is an
  // empty placeholder while the first compute runs in the background — poll.
  computing?: boolean;
  // The window the view was scoped to (server-resolved for a preset, echoed
  // for a custom range, both null for all-time) and the preset requested.
  window_start?: string | null;
  window_end?: string | null;
  preset?: WindowPreset | null;
  heat_rules?: ClusterHeatRules;
  // preset key -> trailing days
  window_presets?: Record<string, number>;
}

export interface ClustersParams {
  dimension?: string;
  // ISO-8601 UTC bounds; omit for "all time". Mutually exclusive with preset.
  window_start?: string;
  window_end?: string;
  // Fixed trailing window (7d | 14d | 30d), resolved by the backend.
  preset?: WindowPreset;
  algorithm?: 'auto' | 'hdbscan' | 'kmeans' | 'agglomerative';
  min_cluster_size?: number;
  k?: number;
  // Noise handling for vector clustering; omit for the backend default.
  outlier_strategy?: 'keep' | 'reassign' | 'subcluster';
}

// One full signal — the drill-down modal reads this by id (the /clusters
// payload carries only 240-char snippets). Backend: GET /admin/signals/signals/{id}.
export interface SignalDetail {
  id: string;
  observation: string;
  significance: string;
  products: string[];
  accounts: string[];
  themes: string[];
  context: string;
  source: string;
  created_at: string;
  metadata: Record<string, unknown>;
}

const BASE = '/admin/signals/clusters';

export const signalsClustersApi = {
  list: async (params?: ClustersParams): Promise<ClustersResponse> => {
    const r = await api.get<ClustersResponse>(BASE, { params });
    return r.data;
  },

  getSignal: async (id: string): Promise<SignalDetail> => {
    const r = await api.get<SignalDetail>(
      `/admin/signals/signals/${encodeURIComponent(id)}`,
    );
    return r.data;
  },
};
